import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, 
  Users, 
  FileText, 
  DollarSign,
  Plus,
  Eye,
  Calendar,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Edit2,
  Download,
  FileCheck,
  FileSearch,
  Package,
  ChevronDown,
  ChevronUp,
  Bell,
  Zap,
  Settings,
  Info,
  ChevronsRight,
  CheckCircle2,
  Clock3,
  Calendar as CalendarIcon,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Search,
  Tag,
  ShoppingCart,
  TrendingDown,
  DollarSign as DollarIcon,
  CreditCard,
  Activity
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import './Dashboard.css';
import { useClients } from '../contexts/ClientsContext';
import { useSettings } from '../contexts/SettingsContext';
import { useProducts } from '../contexts/ProductsContext';
import { useDocuments } from '../contexts/DocumentsContext';
import { useEmployees } from '../contexts/EmployeesContext';
import { formatDZD } from '../utils/currency';
import eventEmitter, { EVENT_TYPES } from '../contexts/ContextEvents';
import { initializeSampleData } from '../utils/sampleData';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const Dashboard = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayPopup, setShowDayPopup] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(3);
  const [notifications, setNotifications] = useState([]);
  const eventListenersRef = useRef([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Chart data states
  const [revenueData, setRevenueData] = useState({
    labels: [],
    datasets: []
  });
  const [expensesData, setExpensesData] = useState({
    labels: [],
    datasets: []
  });
  const [categoryDistributionData, setCategoryDistributionData] = useState({
    labels: [],
    datasets: []
  });
  const [profitTrend, setProfitTrend] = useState({
    labels: [],
    datasets: []
  });
  
  // KPI metrics
  const [kpiData, setKpiData] = useState({
    totalRevenue: 0,
    pendingPayments: 0,
    averageInvoiceValue: 0,
    conversionRate: 0,
    clientRetentionRate: 0
  });
  
  // Import data from contexts
  const { clients, suppliers, getAllPartners, getActiveClients } = useClients();
  const { settings } = useSettings();
  const { products, getActiveProducts, getStockValue } = useProducts();
  const { documents, getRecentDocuments, getDocumentsByStatus } = useDocuments();
  const { employees, getActiveEmployees, getTotalSalaries } = useEmployees();
  
  // Local state for dynamic data
  const [stats, setStats] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [documentStatus, setDocumentStatus] = useState({
    paid: 0,
    pending: 0,
    overdue: 0
  });
  
  // Display info for employees
  const [employeeStats, setEmployeeStats] = useState({
    total: 0,
    active: 0,
    departments: 0,
    totalSalaries: 0
  });

  // Handle task state
  const [tasks, setTasks] = useState([
    { id: 1, text: 'Envoyer les factures du mois', completed: false, priority: 'high' },
    { id: 2, text: 'Mettre à jour le catalogue produits', completed: true, priority: 'medium' },
    { id: 3, text: 'Préparer les devis pour le client Technolabs', completed: false, priority: 'high' },
    { id: 4, text: 'Relancer les factures en retard', completed: false, priority: 'urgent' },
    { id: 5, text: 'Vérifier les stocks de produits', completed: false, priority: 'medium' },
  ]);
  
  // Function to update all dashboard data
  const refreshDashboardData = useCallback(() => {
    setIsRefreshing(true);
    
    try {
      // Direct data retrieval - use EXACTLY the same data that's available in other components
      // Don't modify or filter the data here to ensure consistency with other pages
      
      // Get documents directly from the context
      const allDocuments = documents;
      
      // Get recent documents directly, just sort them by date
      const sortedRecentDocuments = [...allDocuments]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5);
      setRecentDocuments(sortedRecentDocuments);
      
      // Calculate document status distribution - use exact same logic as Documents page
      const paidDocs = allDocuments.filter(doc => doc.status === 'paid').length;
      const pendingDocs = allDocuments.filter(doc => doc.status === 'pending').length;
      const overdueDocs = allDocuments.filter(doc => doc.status === 'overdue').length;
    
    setDocumentStatus({
        paid: paidDocs,
        pending: pendingDocs,
        overdue: overdueDocs
      });
      
      // Get all clients without any filtering
      const allClients = clients;
      
      // Calculate top clients based on total amount, exactly as shown in Clients page
      const sortedTopClients = [...allClients]
        .sort((a, b) => b.totalAmount - a.totalAmount)
        .slice(0, 3);
      setTopClients(sortedTopClients);
      
      // Get all products without any filtering
      const allProducts = products;
      
      // Calculate top products based on price, exactly as shown in Products page
      const sortedTopProducts = [...allProducts]
        .sort((a, b) => b.price - a.price)
        .slice(0, 3);
      setTopProducts(sortedTopProducts);
      
      // Calculate employee stats - use exact same values as Employees page
      const allEmployees = employees;
      const activeEmployeeCount = allEmployees.filter(emp => emp.status === 'active').length;
      const departmentCount = new Set(allEmployees.map(e => e.department)).size;
      const totalSalaryAmount = allEmployees.reduce((sum, emp) => sum + (emp.salary || 0), 0);
      
    setEmployeeStats({
        total: allEmployees.length,
        active: activeEmployeeCount,
        departments: departmentCount,
        totalSalaries: totalSalaryAmount
      });
      
      // Calculate statistics for the metrics cards
      calculateStatsFromRawData(allDocuments, allClients, allProducts, allEmployees);
      
      // Prepare chart data from raw data
      prepareRevenueData(allDocuments);
      prepareExpensesData(allEmployees, allDocuments);
      prepareCategoryDistribution(allDocuments);
      prepareProfitTrend(allDocuments);
      calculateKPIs(allDocuments, allClients);
      
      // Update refresh timestamp
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    clients, 
    documents, 
    employees, 
    products
  ]);
  
  // Update data when dependencies change or time range changes
  useEffect(() => {
    refreshDashboardData();
  }, [refreshDashboardData, timeRange]);
  
  // Initialize sample data if needed
  useEffect(() => {
    // Initialize sample data if there's no data yet
    const docsInStorage = localStorage.getItem('facit_documents');
    const productsInStorage = localStorage.getItem('facit_products');
    const clientsInStorage = localStorage.getItem('facit_clients');
    
    if (!docsInStorage || !productsInStorage || !clientsInStorage) {
      console.log('Initializing sample data...');
      initializeSampleData();
      // Refresh the dashboard once initialized
      setTimeout(refreshDashboardData, 300);
    }
  }, [refreshDashboardData]);
  
  // Set up an interval to refresh data periodically (every 2 minutes)
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshDashboardData();
    }, 120000); // 2 minutes
    
    return () => clearInterval(intervalId);
  }, [refreshDashboardData]);
  
  // Set up event listeners for real-time updates
  useEffect(() => {
    const setupEventListeners = () => {
      // Keep track of listeners to remove them later
      const listeners = [];
      
      // Listen for any data changes
      const dataChangeListener = (dataType) => {
        console.log(`Data changed: ${dataType}`);
        refreshDashboardData();
      };
      listeners.push(eventEmitter.on(EVENT_TYPES.DATA_CHANGED, dataChangeListener));
      
      // Listen for specific document events
      const docStatusChangeListener = (data) => {
        console.log('Document status changed:', data);
        addNotification({
          type: data.newStatus === 'paid' ? 'success' : (data.newStatus === 'overdue' ? 'warning' : 'info'),
          title: `Statut de document changé`,
          message: `Document #${data.documentId} est maintenant ${data.newStatus}`,
          time: new Date()
        });
        refreshDashboardData();
      };
      listeners.push(eventEmitter.on(EVENT_TYPES.DOCUMENT_STATUS_CHANGED, docStatusChangeListener));
      
      // Listen for product stock changes
      const stockChangeListener = (data) => {
        console.log('Stock changed:', data);
        if (data.newStock !== null && typeof data.newStock === 'number' && data.newStock <= 5 && data.newStock > 0) {
          const product = products.find(p => p.id === data.productId);
          if (product) {
            addNotification({
              type: 'warning',
              title: 'Stock faible',
              message: `Le produit "${product.name}" n'a plus que ${data.newStock} en stock`,
              time: new Date()
            });
          }
        }
        refreshDashboardData();
      };
      listeners.push(eventEmitter.on(EVENT_TYPES.PRODUCT_STOCK_CHANGED, stockChangeListener));
      
      // Listen for new clients
      const clientCreateListener = (client) => {
        addNotification({
          type: 'info',
          title: 'Nouveau client',
          message: `Le client ${client.name} a été ajouté`,
          time: new Date()
        });
        refreshDashboardData();
      };
      listeners.push(eventEmitter.on(EVENT_TYPES.CLIENT_CREATED, clientCreateListener));
      
      // Store listeners in ref so we can clean them up later
      eventListenersRef.current = listeners;
    };
    
    setupEventListeners();
    
    // Clean up event listeners
    return () => {
      eventListenersRef.current.forEach(removeListener => removeListener());
      eventListenersRef.current = [];
    };
  }, [products, refreshDashboardData]);
  
  // Function to add a notification
  const addNotification = useCallback((notification) => {
    setNotifications(prev => {
      const newNotifications = [{
        id: Date.now(),
        read: false,
        ...notification
      }, ...prev].slice(0, 20); // Keep only the 20 most recent notifications
      
      // Update unread count
      setNotificationCount(newNotifications.filter(n => !n.read).length);
      
      return newNotifications;
    });
  }, []);
  
  // Function to mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(notification => ({ ...notification, read: true })));
    setNotificationCount(0);
  }, []);
  
  // Function to calculate time ago for notifications
  const timeAgo = (date) => {
    if (!(date instanceof Date)) {
      date = new Date(date);
    }
    
    const seconds = Math.floor((new Date() - date) / 1000);
    
    let interval = seconds / 31536000; // seconds in a year
    if (interval > 1) {
      return `Il y a ${Math.floor(interval)} an${Math.floor(interval) > 1 ? 's' : ''}`;
    }
    
    interval = seconds / 2592000; // seconds in a month
    if (interval > 1) {
      return `Il y a ${Math.floor(interval)} mois`;
    }
    
    interval = seconds / 86400; // seconds in a day
    if (interval > 1) {
      return `Il y a ${Math.floor(interval)} jour${Math.floor(interval) > 1 ? 's' : ''}`;
    }
    
    interval = seconds / 3600; // seconds in an hour
    if (interval > 1) {
      return `Il y a ${Math.floor(interval)} heure${Math.floor(interval) > 1 ? 's' : ''}`;
    }
    
    interval = seconds / 60; // seconds in a minute
    if (interval > 1) {
      return `Il y a ${Math.floor(interval)} minute${Math.floor(interval) > 1 ? 's' : ''}`;
    }
    
    return 'À l\'instant';
  };
  
  // Calculate dashboard statistics from raw data
  const calculateStatsFromRawData = (allDocuments, allClients, allProducts, allEmployees) => {
    // Set time range in days
    let days = 30;
    switch(timeRange) {
      case '7d': days = 7; break;
      case '30d': days = 30; break;
      case '90d': days = 90; break;
      case '1y': days = 365; break;
      default: days = 30;
    }
    
    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    // Total revenue from paid invoices - exactly as calculated in other pages
    const totalRevenue = allDocuments
      .filter(doc => doc.type === 'invoice' && doc.status === 'paid')
      .filter(doc => {
        const docDate = new Date(doc.date);
        return docDate >= startDate && docDate <= endDate;
      })
      .reduce((sum, doc) => sum + (doc.amount || 0), 0);
    
    // Client count - exactly as shown in Clients page
    const activeClientCount = allClients.filter(client => client.status === 'active').length;
    
    // Document count - exactly as shown in Documents page
    const documentCount = allDocuments.filter(doc => {
          const docDate = new Date(doc.date);
      return docDate >= startDate && docDate <= endDate;
    }).length;
    
    // Stock value - exactly as calculated in Products page
    const stockValue = allProducts
      .filter(product => product.type === 'product' && product.stock !== null)
      .reduce((sum, product) => sum + (product.price * product.stock), 0);
    
    // Set stats array for cards display
    setStats([
      { 
        label: 'Chiffre d\'affaires', 
        value: formatDZD(totalRevenue),
        description: `${days} derniers jours`, 
        details: 'Factures payées',
        change: '+12%', // This would be calculated from historical data in a real app
        changeType: 'increase',
        icon: DollarSign,
        color: '#3b82f6'
      },
      { 
        label: 'Clients', 
        value: activeClientCount,
        description: 'Clients actifs',
        details: `${Math.round(activeClientCount * 0.6)} retours`,
        change: '+5%', // This would be calculated from historical data in a real app
        changeType: 'increase', 
        icon: Users,
        color: '#10b981'
      },
      { 
        label: 'Documents', 
        value: documentCount,
        description: `${days} derniers jours`, 
        details: `${allDocuments.filter(doc => doc.status === 'pending').length} en attente`,
        change: '+18%', // This would be calculated from historical data in a real app
        changeType: 'increase',
        icon: FileText,
        color: '#8b5cf6'
      },
      { 
        label: 'Valeur Stock', 
        value: formatDZD(stockValue),
        description: 'Produits disponibles', 
        details: `${allProducts.filter(p => p.type === 'product' && p.status === 'active').length} produits`,
        change: '-3%', // This would be calculated from historical data in a real app
        changeType: 'decrease', 
        icon: Package,
        color: '#f59e0b'
      }
    ]);
  };

  // Generate revenue chart data
  const prepareRevenueData = (allDocuments) => {
    // Get appropriate time range
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365;
    
    // Generate date labels
    const labels = [];
    const values = [];
    const endDate = new Date();
    
    // For shorter periods, show daily data
    if (days <= 30) {
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        labels.push(date.toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'}));
        
        // Calculate revenue for this day
        const dayRevenue = allDocuments
          .filter(doc => doc.type === 'invoice' && doc.status === 'paid')
          .filter(doc => {
            const docDate = new Date(doc.date);
            return docDate.toDateString() === date.toDateString();
          })
          .reduce((sum, doc) => sum + (doc.amount || 0), 0);
          
        values.push(dayRevenue);
      }
    } else {
      // For longer periods, aggregate by week or month
      const interval = days > 90 ? 'month' : 'week';
      const intervals = days > 90 ? 12 : Math.ceil(days / 7);
      
      for (let i = intervals - 1; i >= 0; i--) {
        const date = new Date();
        if (interval === 'month') {
          date.setMonth(date.getMonth() - i);
          labels.push(date.toLocaleDateString('fr-FR', {month: 'short'}));
        } else {
          date.setDate(date.getDate() - (i * 7));
          labels.push(`S${Math.ceil((date.getDate() + (new Date(date.getFullYear(), date.getMonth(), 1).getDay() - 1)) / 7)}`);
        }
        
        // Calculate start and end of interval
        const intervalStart = new Date(date);
        const intervalEnd = new Date(date);
        if (interval === 'month') {
          intervalStart.setDate(1);
          intervalEnd.setMonth(intervalEnd.getMonth() + 1);
          intervalEnd.setDate(0);
        } else {
          intervalStart.setDate(intervalStart.getDate() - intervalStart.getDay() + 1);
          intervalEnd.setDate(intervalStart.getDate() + 6);
        }
        
        // Calculate revenue for this interval
        const intervalRevenue = allDocuments
          .filter(doc => doc.type === 'invoice' && doc.status === 'paid')
          .filter(doc => {
            const docDate = new Date(doc.date);
            return docDate >= intervalStart && docDate <= intervalEnd;
          })
          .reduce((sum, doc) => sum + (doc.amount || 0), 0);
          
        values.push(intervalRevenue);
      }
    }
    
    setRevenueData({
      labels,
      datasets: [
        {
          label: 'Chiffre d\'affaires',
          data: values,
          backgroundColor: 'rgba(59, 130, 246, 0.2)',
          borderColor: '#3b82f6',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    });
  };
  
  // Generate expenses chart data
  const prepareExpensesData = (allEmployees, allDocuments) => {
    // For the demo, we'll simulate some expense data
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365;
    
    // Generate date labels - reuse the same logic as revenue
    const labels = [];
    const endDate = new Date();
    
    // Generate random expense data that follows a realistic pattern
    const operatingExpenses = [];
    const salaryExpenses = [];
    const supplierExpenses = [];
    
    // For shorter periods, show daily data
    if (days <= 30) {
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        labels.push(date.toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'}));
        
        // Simulate expense data with some randomness but also patterns
        const dayOfWeek = date.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        // Operating expenses are fairly consistent with small variations
        operatingExpenses.push(isWeekend ? 2000 + Math.random() * 1000 : 5000 + Math.random() * 2000);
        
        // Salary expenses only on certain days (e.g., end of month)
        salaryExpenses.push(date.getDate() >= 25 && date.getDate() <= 30 ? 20000 + Math.random() * 5000 : 0);
        
        // Supplier payments vary more
        supplierExpenses.push(Math.random() > 0.7 ? 15000 + Math.random() * 10000 : 0);
      }
    } else {
      // For longer periods, aggregate by week or month
      const interval = days > 90 ? 'month' : 'week';
      const intervals = days > 90 ? 12 : Math.ceil(days / 7);
      
      for (let i = intervals - 1; i >= 0; i--) {
        const date = new Date();
        if (interval === 'month') {
          date.setMonth(date.getMonth() - i);
          labels.push(date.toLocaleDateString('fr-FR', {month: 'short'}));
          
          // Monthly expenses
          operatingExpenses.push(150000 + Math.random() * 30000);
          salaryExpenses.push(allEmployees.reduce((sum, emp) => sum + (emp.salary || 0), 0) || 200000 + Math.random() * 50000);
          supplierExpenses.push(100000 + Math.random() * 80000);
        } else {
          date.setDate(date.getDate() - (i * 7));
          labels.push(`S${Math.ceil((date.getDate() + (new Date(date.getFullYear(), date.getMonth(), 1).getDay() - 1)) / 7)}`);
          
          // Weekly expenses
          operatingExpenses.push(35000 + Math.random() * 10000);
          salaryExpenses.push(i % 4 === 0 ? (allEmployees.reduce((sum, emp) => sum + (emp.salary || 0), 0) || 200000) / 4 : 0);
          supplierExpenses.push(25000 + Math.random() * 20000);
        }
      }
    }
    
    setExpensesData({
      labels,
      datasets: [
        {
          label: 'Charges d\'exploitation',
          data: operatingExpenses,
          backgroundColor: '#3b82f6',
          barPercentage: 0.7,
          categoryPercentage: 0.7
        },
        {
          label: 'Salaires',
          data: salaryExpenses,
          backgroundColor: '#f59e0b',
          barPercentage: 0.7,
          categoryPercentage: 0.7
        },
        {
          label: 'Fournisseurs',
          data: supplierExpenses,
          backgroundColor: '#10b981',
          barPercentage: 0.7,
          categoryPercentage: 0.7
        }
      ]
    });
  };
  
  // Generate category distribution chart
  const prepareCategoryDistribution = (allDocuments) => {
    // Group documents by category and calculate totals
    const categoryTotals = allDocuments.reduce((acc, doc) => {
      if (doc.category && doc.amount) {
        acc[doc.category] = (acc[doc.category] || 0) + doc.amount;
      }
      return acc;
    }, {});
    
    // Convert to arrays for chart data
    const categories = Object.keys(categoryTotals);
    const amounts = Object.values(categoryTotals);
    
    // Generate colors for each category
    const backgroundColors = [
      'rgba(59, 130, 246, 0.7)',  // Blue
      'rgba(16, 185, 129, 0.7)',  // Green
      'rgba(245, 158, 11, 0.7)',  // Yellow
      'rgba(139, 92, 246, 0.7)',  // Purple
      'rgba(239, 68, 68, 0.7)',   // Red
      'rgba(14, 165, 233, 0.7)',  // Sky Blue
    ];
    
    // Limit to top 6 categories
    const topCategories = categories
      .map((category, index) => ({ category, amount: amounts[index] }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 6);
    
    setCategoryDistributionData({
      labels: topCategories.map(item => item.category),
      datasets: [
        {
          data: topCategories.map(item => item.amount),
          backgroundColor: backgroundColors.slice(0, topCategories.length),
          borderWidth: 0
        }
      ]
    });
  };
  
  // Generate profit trend data
  const prepareProfitTrend = (allDocuments) => {
    // Simplified profit calculation based on revenues and expenses
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365;
    
    // Generate date labels - reuse the same logic as revenue
    const labels = [];
    const revenueValues = [];
    const expenseValues = [];
    const profitValues = [];
    
    // For shorter periods, show daily data
    if (days <= 30) {
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        labels.push(date.toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'}));
        
        // Calculate revenue for this day
        const dayRevenue = allDocuments
          .filter(doc => doc.type === 'invoice' && doc.status === 'paid')
          .filter(doc => {
            const docDate = new Date(doc.date);
            return docDate.toDateString() === date.toDateString();
          })
          .reduce((sum, doc) => sum + (doc.amount || 0), 0);
          
        revenueValues.push(dayRevenue);
        
        // Simulate expenses (in a real app, these would come from actual expense records)
        const dayExpense = dayRevenue * (0.4 + Math.random() * 0.3); // 40-70% of revenue
        expenseValues.push(dayExpense);
        
        // Calculate profit
        profitValues.push(dayRevenue - dayExpense);
      }
    } else {
      // Similar aggregation logic as before
      const interval = days > 90 ? 'month' : 'week';
      const intervals = days > 90 ? 12 : Math.ceil(days / 7);
      
      for (let i = intervals - 1; i >= 0; i--) {
        const date = new Date();
        if (interval === 'month') {
          date.setMonth(date.getMonth() - i);
          labels.push(date.toLocaleDateString('fr-FR', {month: 'short'}));
        } else {
          date.setDate(date.getDate() - (i * 7));
          labels.push(`S${Math.ceil((date.getDate() + (new Date(date.getFullYear(), date.getMonth(), 1).getDay() - 1)) / 7)}`);
        }
        
        // Calculate start and end of interval
        const intervalStart = new Date(date);
        const intervalEnd = new Date(date);
        if (interval === 'month') {
          intervalStart.setDate(1);
          intervalEnd.setMonth(intervalEnd.getMonth() + 1);
          intervalEnd.setDate(0);
        } else {
          intervalStart.setDate(intervalStart.getDate() - intervalStart.getDay() + 1);
          intervalEnd.setDate(intervalStart.getDate() + 6);
        }
        
        // Calculate revenue for this interval
        const intervalRevenue = allDocuments
          .filter(doc => doc.type === 'invoice' && doc.status === 'paid')
          .filter(doc => {
            const docDate = new Date(doc.date);
            return docDate >= intervalStart && docDate <= intervalEnd;
          })
          .reduce((sum, doc) => sum + (doc.amount || 0), 0);
          
        revenueValues.push(intervalRevenue);
        
        // Simulate expenses
        const intervalExpense = intervalRevenue * (0.5 + Math.random() * 0.2); // 50-70% of revenue
        expenseValues.push(intervalExpense);
        
        // Calculate profit
        profitValues.push(intervalRevenue - intervalExpense);
      }
    }
    
    setProfitTrend({
      labels,
      datasets: [
        {
          label: 'Revenus',
          data: revenueValues,
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderColor: '#10b981',
          borderWidth: 2,
          tension: 0.4,
          fill: false
        },
        {
          label: 'Dépenses',
          data: expenseValues,
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderColor: '#ef4444',
          borderWidth: 2,
          tension: 0.4,
          fill: false
        },
        {
          label: 'Bénéfices',
          data: profitValues,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: '#3b82f6',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    });
  };
  
  // Calculate KPI metrics
  const calculateKPIs = (allDocuments, allClients) => {
    // Set time range in days
    let days = 30;
    switch(timeRange) {
      case '7d': days = 7; break;
      case '30d': days = 30; break;
      case '90d': days = 90; break;
      case '1y': days = 365; break;
      default: days = 30;
    }
    
    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    // Filter documents in the date range
    const periodDocuments = allDocuments.filter(doc => {
      const docDate = new Date(doc.date);
      return docDate >= startDate && docDate <= endDate;
    });
    
    // Total revenue
    const totalRevenue = periodDocuments
      .filter(doc => doc.type === 'invoice' && doc.status === 'paid')
      .reduce((sum, doc) => sum + (doc.amount || 0), 0);
    
    // Pending payments
    const pendingPayments = periodDocuments
      .filter(doc => doc.type === 'invoice' && doc.status === 'pending')
      .reduce((sum, doc) => sum + (doc.amount || 0), 0);
    
    // Average invoice value
    const invoices = periodDocuments.filter(doc => doc.type === 'invoice');
    const averageInvoiceValue = invoices.length > 0
      ? invoices.reduce((sum, doc) => sum + (doc.amount || 0), 0) / invoices.length
      : 0;
    
    // Quote to invoice conversion rate
    const quotes = allDocuments.filter(doc => doc.type === 'quote');
    const acceptedQuotes = quotes.filter(doc => doc.status === 'accepted');
    const conversionRate = quotes.length > 0 
      ? (acceptedQuotes.length / quotes.length) * 100 
      : 0;
    
    // Client retention rate (simplified calculation)
    // In a real app, this would compare clients with repeat orders over time
    const activeClients = allClients.filter(client => client.status === 'active').length;
    const totalClients = allClients.length;
    const clientRetentionRate = totalClients > 0 
      ? (activeClients / totalClients) * 100 
      : 0;
    
    setKpiData({
      totalRevenue,
      pendingPayments,
      averageInvoiceValue,
      conversionRate,
      clientRetentionRate
    });
  };
  
  const toggleTaskComplete = (taskId) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };
  
  // Generate calendar data based on documents
  const generateCalendarData = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Calculate first day of month and how many days to show from previous month
    const firstDayOfMonth = new Date(year, month, 1);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysFromPrevMonth = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1; // Adjust for Monday start
    
    // Calculate days in current, previous, and next month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    
    const days = [];
    
    // Add days from previous month
    for (let i = daysInPrevMonth - daysFromPrevMonth + 1; i <= daysInPrevMonth; i++) {
      const date = new Date(year, month - 1, i);
      days.push({
        day: i,
        date: date,
        isCurrentMonth: false,
        isToday: isToday(date),
        hasDocuments: checkForDocumentsOnDay(date, documents)
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        day: i,
        date: date,
        isCurrentMonth: true,
        isToday: isToday(date),
        hasDocuments: checkForDocumentsOnDay(date, documents)
      });
    }
    
    // Calculate how many days needed from next month to complete the grid
    const daysToShow = 42; // 6 rows of 7 days
    const daysFromNextMonth = daysToShow - days.length;
    
    // Add days from next month
    for (let i = 1; i <= daysFromNextMonth; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        day: i,
        date: date,
        isCurrentMonth: false,
        isToday: isToday(date),
        hasDocuments: checkForDocumentsOnDay(date, documents)
      });
    }
    
    return days;
  };
  
  // Helper function to check if there are documents on a specific day
  const checkForDocumentsOnDay = (date, allDocuments) => {
    return allDocuments.some(doc => {
      const docDate = new Date(doc.date);
      return docDate.getDate() === date.getDate() && 
             docDate.getMonth() === date.getMonth() && 
             docDate.getFullYear() === date.getFullYear();
    });
  };
  
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };
  
  // Calendar data
  const calendarDays = generateCalendarData();
  
  const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  
  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const handleActionClick = (action) => {
    if (action.path) {
      navigate(action.path);
    } else if (action.action === 'refresh') {
      // Afficher un message de chargement
      const button = document.querySelector('[data-action="refresh"]');
      if (button) {
        button.style.opacity = '0.6';
        button.innerHTML = '<div style="display: flex; align-items: center; gap: 8px;"><div style="width: 16px; height: 16px; border: 2px solid #64748b; border-top: 2px solid transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>Actualisation...</div>';
      }
      
      // Simuler un délai puis recharger
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  // Documents data for calendar days
  const documentsData = {
    12: [
      { type: 'Facture', code: 'FAC-2024-089', client: 'TechnoServices', deadline: '16h00', priority: 'urgent' },
      { type: 'Devis', code: 'DEV-2024-045', client: 'ModernIT Solutions', deadline: '17h30', priority: 'warning' }
    ],
    13: [
      { type: 'Bon de commande', code: 'BC-2024-023', client: 'Digital Corp', deadline: '16h00', priority: 'normal' },
      { type: 'Facture Pro Forma', code: 'PRO-2024-012', client: 'InnoTech', deadline: '15h00', priority: 'normal' },
      { type: 'Reçu', code: 'REC-2024-067', client: 'SmartSystems', deadline: '14h00', priority: 'urgent' }
    ],
    14: [
      { type: 'Contrat de service', code: 'CST-2024-008', client: 'Enterprise Ltd', deadline: '10h00', priority: 'warning' }
    ],
    15: [
      { type: 'Facture', code: 'FAC-2024-090', client: 'WebServices', deadline: '12h00', priority: 'urgent' },
      { type: 'Attestation', code: 'ATT-2024-034', client: 'GlobalTech', deadline: '16h00', priority: 'urgent' },
      { type: 'Rapport d\'activité', code: 'RAP-2024-015', client: 'Innovation SA', deadline: '14h00', priority: 'warning' },
      { type: 'Certificat', code: 'CER-2024-021', client: 'TechFlow', deadline: '11h00', priority: 'normal' }
    ],
    16: [
      { type: 'Facture de prestation', code: 'FPR-2024-056', client: 'DataSoft', deadline: '13h00', priority: 'normal' },
      { type: 'Devis technique', code: 'DVT-2024-078', client: 'SystemCorp', deadline: '15h30', priority: 'normal' }
    ],
    18: [
      { type: 'Bon de livraison', code: 'BDL-2024-091', client: 'LogiTech', deadline: '09h00', priority: 'warning' },
      { type: 'Facture d\'acompte', code: 'FAC-2024-092', client: 'StartupXYZ', deadline: '18h00', priority: 'normal' }
    ],
    20: [
      { type: 'Contrat maintenance', code: 'CMT-2024-033', client: 'MegaCorp', deadline: '14h00', priority: 'normal' }
    ],
    22: [
      { type: 'Facture rectificative', code: 'FRC-2024-087', client: 'TechServices', deadline: '10h00', priority: 'urgent' },
      { type: 'Devis de réparation', code: 'DVR-2024-041', client: 'RepairCo', deadline: '16h00', priority: 'warning' },
      { type: 'Attestation de conformité', code: 'ATC-2024-019', client: 'QualityInc', deadline: '12h00', priority: 'normal' }
    ]
  };

  const handleDayClick = (day) => {
    if (documentsData[day]) {
      setSelectedDay(day);
      setShowDayPopup(true);
    }
  };

  const closeDayPopup = () => {
    setShowDayPopup(false);
    setSelectedDay(null);
  };

  const quickActions = [
    {
      title: t('dashboard.quick_actions.create_document'),
      description: t('dashboard.quick_actions.create_desc'),
      icon: Plus,
      color: '#3b82f6',
      path: '/document-creation'
    },
    {
      title: t('dashboard.quick_actions.view_archives'),
      description: t('dashboard.quick_actions.archives_desc'),
      icon: Eye,
      color: '#10b981',
      path: '/archives'
    },
    {
      title: t('dashboard.quick_actions.settings'),
      description: t('dashboard.quick_actions.settings_desc'),
      icon: Users,
      color: '#8b5cf6',
      path: '/settings'
    },
    {
      title: t('dashboard.quick_actions.reports'),
      description: t('dashboard.quick_actions.reports_desc'),
      icon: BarChart3,
      color: '#f59e0b',
      path: '/reports'
    }
  ];

  return (
    <div className="dashboard fade-in">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title" style={{ color: '#ffffff' }}>{t('dashboard.title')}</h1>
            <p className="page-subtitle" style={{ color: '#ffffff' }}>
              {t('dashboard.subtitle')}
            </p>
          </div>
          <div className="header-controls">
            <select 
              className="time-range-selector"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="7d">{t('dashboard.time_range.7d')}</option>
              <option value="30d">{t('dashboard.time_range.30d')}</option>
              <option value="90d">{t('dashboard.time_range.90d')}</option>
              <option value="1y">{t('dashboard.time_range.1y')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Dashboard Container */}
      <div className="dashboard-container">
        {/* Quick Actions Section - Moved to appear right after header */}
        <div className="quick-actions-container">
          <div className="section-header">
            <div className="section-title">
              <Zap size={20} />
              <span>Actions Rapides</span>
            </div>
            <div className="section-description">Accès direct aux fonctionnalités principales</div>
          </div>
          <div className="quick-actions-grid">
            <div 
              className="action-card"
              onClick={() => navigate('/document-creation')}
            >
              <div className="action-icon" style={{background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6'}}>
                <FileText size={24} />
              </div>
              <div className="action-content">
                <h3 className="action-title">Créer un document</h3>
                <p className="action-description">Nouveau document commercial</p>
              </div>
              <div className="action-arrow">→</div>
            </div>
            
            <div 
              className="action-card"
              onClick={() => navigate('/clients')}
            >
              <div className="action-icon" style={{background: 'rgba(16, 185, 129, 0.1)', color: '#10b981'}}>
                <Users size={24} />
              </div>
              <div className="action-content">
                <h3 className="action-title">Ajouter un client</h3>
                <p className="action-description">Créer un nouveau client</p>
              </div>
              <div className="action-arrow">→</div>
            </div>
            
            <div 
              className="action-card"
              onClick={() => navigate('/products')}
            >
              <div className="action-icon" style={{background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b'}}>
                <Package size={24} />
              </div>
              <div className="action-content">
                <h3 className="action-title">Gérer le catalogue</h3>
                <p className="action-description">Produits et services</p>
              </div>
              <div className="action-arrow">→</div>
            </div>
            
            <div 
              className="action-card"
              onClick={() => navigate('/archives')}
            >
              <div className="action-icon" style={{background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6'}}>
                <Search size={24} />
              </div>
              <div className="action-content">
                <h3 className="action-title">Rechercher</h3>
                <p className="action-description">Archives et documents</p>
              </div>
              <div className="action-arrow">→</div>
            </div>
          </div>
        </div>
        
        {/* Financial Overview Section - New Section */}
        <div className="financial-overview">
          <h2 className="section-main-title">
            <Activity size={20} />
            <span>Aperçu Financier</span>
          </h2>
          
          <div className="financial-grid">
            {/* KPI Cards */}
            <div className="kpi-cards">
              <div className="kpi-card">
                <div className="kpi-icon" style={{background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6'}}>
                  <DollarIcon size={20} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Total des revenus</div>
                  <div className="kpi-value">{formatDZD(kpiData.totalRevenue)}</div>
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-icon" style={{background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444'}}>
                  <Clock size={20} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Paiements en attente</div>
                  <div className="kpi-value">{formatDZD(kpiData.pendingPayments)}</div>
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-icon" style={{background: 'rgba(16, 185, 129, 0.1)', color: '#10b981'}}>
                  <CreditCard size={20} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Valeur moyenne</div>
                  <div className="kpi-value">{formatDZD(kpiData.averageInvoiceValue)}</div>
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-icon" style={{background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b'}}>
                  <TrendingUp size={20} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Taux de conversion</div>
                  <div className="kpi-value">{kpiData.conversionRate.toFixed(1)}%</div>
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-icon" style={{background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6'}}>
                  <Users size={20} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Fidélisation clients</div>
                  <div className="kpi-value">{kpiData.clientRetentionRate.toFixed(1)}%</div>
                </div>
              </div>
            </div>
            
            {/* Revenue Chart */}
            <div className="chart-container revenue-chart">
              <div className="chart-header">
                <h3>Évolution du Chiffre d'Affaires</h3>
                <div className="chart-legend">
                  <span className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#3b82f6'}}></span>
                    <span>CA</span>
                  </span>
                </div>
              </div>
              <div className="chart-body">
                <Line 
                  data={revenueData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: { mode: 'index', intersect: false }
                    },
                    scales: {
                      y: { 
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M DA';
                            if (value >= 1000) return (value / 1000).toFixed(0) + 'k DA';
                            return value + ' DA';
                          }
                        }
                      },
                      x: { grid: { display: false } }
                    }
                  }} 
                />
              </div>
            </div>
            
            {/* Expenses Chart */}
            <div className="chart-container expenses-chart">
              <div className="chart-header">
                <h3>Répartition des Dépenses</h3>
                <div className="chart-legend">
                  <span className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#3b82f6'}}></span>
                    <span>Opérationnel</span>
                  </span>
                  <span className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#f59e0b'}}></span>
                    <span>Salaires</span>
                  </span>
                  <span className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#10b981'}}></span>
                    <span>Fournisseurs</span>
                  </span>
                </div>
              </div>
              <div className="chart-body">
                <Bar 
                  data={expensesData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      y: {
                        stacked: false,
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                            if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
                            return value;
                          }
                        }
                      },
                      x: { stacked: false, grid: { display: false } }
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Category Distribution Chart */}
            <div className="chart-container category-chart">
              <div className="chart-header">
                <h3>Distribution par Catégorie</h3>
              </div>
              <div className="chart-body doughnut-chart">
                <Doughnut 
                  data={categoryDistributionData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: {
                          boxWidth: 12,
                          padding: 15
                        }
                      }
                    },
                    cutout: '60%'
                  }}
                />
              </div>
            </div>
            
            {/* Profit Trend Chart */}
            <div className="chart-container profit-chart">
              <div className="chart-header">
                <h3>Tendance des Profits</h3>
                <div className="chart-legend">
                  <span className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#10b981'}}></span>
                    <span>Revenus</span>
                  </span>
                  <span className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#ef4444'}}></span>
                    <span>Dépenses</span>
                  </span>
                  <span className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#3b82f6'}}></span>
                    <span>Profits</span>
                  </span>
                </div>
              </div>
              <div className="chart-body">
                <Line 
                  data={profitTrend}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: { mode: 'index', intersect: false }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                            if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
                            return value;
                          }
                        }
                      },
                      x: { grid: { display: false } }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Key Metrics Row */}
        <div className="metrics-row">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
              <div key={index} className="metric-card">
                <div className="metric-content">
                  <div className="metric-header">
                    <h3 className="metric-label">{stat.label}</h3>
                    <div className="metric-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                    <Icon size={20} />
                  </div>
                </div>
                  <div className="metric-value">{stat.value}</div>
                  <div className="metric-description">{stat.description}</div>
                  <div className="metric-details">{stat.details}</div>
                  <div className={`metric-change ${stat.changeType}`}>
                  <TrendingUp size={14} />
                  {stat.change}
                </div>
              </div>
            </div>
          );
        })}
      </div>

        {/* Main Content Columns - Reorganized from 3 to 2 columns */}
        <div className="dashboard-columns two-columns">
          {/* Left Column */}
          <div className="dashboard-column">
            {/* Document Status */}
            <div className="dashboard-panel">
              <div className="panel-header">
                <div className="panel-title">
                  <FileCheck size={18} />
                  <span>État des Documents</span>
                </div>
              </div>
              <div className="panel-body">
                <div className="status-grid">
                  <div className="status-card">
                    <div className="status-icon paid">
                      <CheckCircle2 size={24} />
        </div>
                    <div className="status-info">
                      <div className="status-value">{documentStatus.paid}</div>
                      <div className="status-label">Payé</div>
                        </div>
                      </div>
                  <div className="status-card">
                    <div className="status-icon pending">
                      <Clock3 size={24} />
                    </div>
                    <div className="status-info">
                      <div className="status-value">{documentStatus.pending}</div>
                      <div className="status-label">En attente</div>
                        </div>
                      </div>
                  <div className="status-card">
                    <div className="status-icon overdue">
                      <AlertCircle size={24} />
                        </div>
                    <div className="status-info">
                      <div className="status-value">{documentStatus.overdue}</div>
                      <div className="status-label">En retard</div>
                      </div>
                    </div>
                  </div>
                  
                {/* Bar Chart Visualization */}
                <div className="status-chart">
                  <div className="chart-bar-container">
                    <div 
                      className="chart-bar paid" 
                      style={{ 
                        width: `${Math.max(documentStatus.paid / (documentStatus.paid + documentStatus.pending + documentStatus.overdue || 1) * 100, 5)}%`
                      }}
                    ></div>
                    <div 
                      className="chart-bar pending" 
                      style={{ 
                        width: `${Math.max(documentStatus.pending / (documentStatus.paid + documentStatus.pending + documentStatus.overdue || 1) * 100, 5)}%`
                      }}
                    ></div>
                    <div 
                      className="chart-bar overdue" 
                      style={{ 
                        width: `${Math.max(documentStatus.overdue / (documentStatus.paid + documentStatus.pending + documentStatus.overdue || 1) * 100, 5)}%`
                      }}
                    ></div>
                      </div>
                  <div className="chart-legend">
                    <div className="legend-item">
                      <span className="legend-color paid"></span>
                      <span>Payé</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color pending"></span>
                      <span>En attente</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color overdue"></span>
                      <span>En retard</span>
                    </div>
                </div>
              </div>
              </div>
            </div>

            {/* Top Products */}
            <div className="dashboard-panel">
              <div className="panel-header">
                <div className="panel-title">
                  <Package size={18} />
                  <span>Top Produits/Services</span>
                </div>
              <button className="view-all-btn" onClick={() => navigate('/products')}>
                Catalogue <ChevronsRight size={16} />
              </button>
              </div>
            <div className="panel-body">
              <div className="top-products-list">
                {topProducts.map((product) => (
                  <div key={product.id} className="top-product-item">
                    <div className={`product-icon ${product.type}`}>
                      {product.type === 'product' ? <Package size={18} /> : <Tag size={18} />}
                  </div>
                    <div className="product-info">
                      <div className="product-name">{product.name}</div>
                      <div className="product-type">{product.type === 'product' ? 'Produit' : 'Service'}</div>
                </div>
                    <div className="product-price">
                      {formatDZD(product.price)}
                </div>
                  </div>
                ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="dashboard-column">
            {/* Top Clients */}
            <div className="dashboard-panel">
              <div className="panel-header">
                <div className="panel-title">
                  <Users size={18} />
                  <span>Top Clients</span>
                </div>
                <button className="view-all-btn" onClick={() => navigate('/clients')}>
                  Tous les clients <ChevronsRight size={16} />
                </button>
              </div>
              <div className="panel-body">
                <div className="top-clients-list">
                  {topClients.map((client, index) => (
                    <div key={client.id} className="top-client-item">
                      <div className="client-rank">{index + 1}</div>
                      <div className="client-avatar">
                        {client.name.substring(0, 2).toUpperCase()}
                  </div>
                      <div className="client-info">
                        <div className="client-name">{client.name}</div>
                        <div className="client-details">
                          {client.totalOrders} commandes · {formatDZD(client.totalAmount)}
                  </div>
                </div>
                      <div className={`client-status ${client.status}`}>
                        {client.status === 'active' ? 'Actif' : 'Inactif'}
                  </div>
                </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Mini Calendar */}
            <div className="dashboard-panel calendar-panel">
              <div className="panel-header">
                <div className="panel-title">
                  <CalendarIcon size={18} />
                  <span>Calendrier</span>
                </div>
                <div className="calendar-navigation">
                  <button className="calendar-nav-btn" onClick={handlePrevMonth}>
                    <ChevronUp size={16} />
                  </button>
                  <span className="current-month">{months[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
                  <button className="calendar-nav-btn" onClick={handleNextMonth}>
                    <ChevronDown size={16} />
                  </button>
                </div>
              </div>
              <div className="panel-body">
                <div className="mini-calendar">
                  <div className="calendar-weekdays">
                    <span>L</span>
                    <span>M</span>
                    <span>M</span>
                    <span>J</span>
                    <span>V</span>
                    <span>S</span>
                    <span>D</span>
                  </div>
                  <div className="calendar-days">
                    {calendarDays.map((day, index) => (
                      <div 
                        key={index}
                        className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} 
                                  ${day.isToday ? 'today' : ''} 
                                  ${day.hasDocuments ? 'has-events' : ''}`}
                        onClick={() => day.isCurrentMonth && day.hasDocuments && handleDayClick(day.day)}
                      >
                        {day.day}
                        {day.hasDocuments && <div className="day-indicator"></div>}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="upcoming-events">
                  <h3>Prochains événements</h3>
                  <div className="events-list">
                    {recentDocuments.slice(0, 2).map((doc, idx) => (
                      <div key={idx} className="event-item">
                        <div className="event-date">{doc.date}</div>
                        <div className="event-title">{doc.title}</div>
                  </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Summary Section */}
            <div className="dashboard-panel">
              <div className="panel-header">
                <div className="panel-title">
                  <Users size={18} />
                  <span>Résumé des Employés</span>
                </div>
                <button className="view-all-btn" onClick={() => navigate('/employees')}>
                  Équipe <ChevronsRight size={16} />
                </button>
              </div>
              <div className="panel-body">
                <div className="employee-summary">
                  <div className="employee-stats-grid">
                    <div className="employee-stat-item">
                      <div className="stat-circle" style={{ backgroundColor: "#4f46e5" }}>
                        <span>{employeeStats.total}</span>
                      </div>
                      <div className="stat-detail">
                        <span className="stat-title">Total</span>
                      </div>
                    </div>
                    
                    <div className="employee-stat-item">
                      <div className="stat-circle" style={{ backgroundColor: "#10b981" }}>
                        <span>{employeeStats.active}</span>
                      </div>
                      <div className="stat-detail">
                        <span className="stat-title">Actifs</span>
                      </div>
                    </div>
                    
                    <div className="employee-stat-item">
                      <div className="stat-circle" style={{ backgroundColor: "#f59e0b" }}>
                        <span>{employeeStats.departments}</span>
                      </div>
                      <div className="stat-detail">
                        <span className="stat-title">Départ.</span>
                      </div>
                    </div>
                    
                    <div className="employee-stat-item">
                      <div className="stat-circle" style={{ backgroundColor: "#ef4444" }}>
                        <span>{formatDZD(employeeStats.totalSalaries).slice(0, -6)}</span>
                      </div>
                      <div className="stat-detail">
                        <span className="stat-title">Masse Sal.</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="employee-recent-list">
                    <h4>Employés récents</h4>
                    <ul>
                      {employees.slice(0, 3).map(employee => (
                        <li key={employee.id} className="employee-list-item">
                          <div className="employee-avatar">
                            {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                          </div>
                          <div className="employee-info">
                            <span className="employee-name">{employee.firstName} {employee.lastName}</span>
                            <span className="employee-position">{employee.position}</span>
                          </div>
                          <div className="employee-status">
                            <span className={`status-indicator ${employee.status}`}></span>
                            {employee.status === 'active' ? 'Actif' : 'Inactif'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
          </div>
    </div>
  );
};

export default Dashboard; 